import React, { useState, useCallback } from 'react';
import { Share2, Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToastContext } from '../../contexts/ToastContext';
import { API_URL } from '../../utils/apiBase';
import { trackEvent } from '../../utils/tracking';

/**
 * "Share your itinerary" CTA.
 *
 * One tap → POST /api/trips/share with the current tripPlan + hotels +
 * flights → backend returns a short slug → we open the native Web Share
 * sheet (mobile) OR copy the share URL (desktop), then show a transient
 * "Copied!" confirmation.
 *
 * Why this matters: travelers love showing off their planning. Every
 * shared link is a free conversion funnel — the recipient lands on a
 * preview page with a "Plan your own trip with TravelAI" CTA. Pattern
 * borrowed from Hopper's "Send to friend" + Airbnb's wishlist share.
 */
export default function ShareTripButton({ tripPlan, hotels, flights, message }) {
  const { t } = useLanguage();
  const toast = useToastContext();
  const [state, setState] = useState('idle'); // 'idle' | 'sharing' | 'copied'

  const handleShare = useCallback(async () => {
    if (!tripPlan?.destination) return;
    setState('sharing');
    try {
      const res = await fetch(`${API_URL}/api/trips/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tripPlan,
          hotels: hotels || [],
          flights: flights || [],
          message: message || '',
        }),
      });
      const body = await res.json();
      if (!body?.success || !body?.data?.slug) {
        throw new Error(body?.error || 'Share failed');
      }
      const shareUrl = `${window.location.origin}${body.data.path}`;
      const dest = tripPlan.destinationEn || tripPlan.destination;

      // Try native Web Share API first (mobile + modern desktop browsers).
      // Falls back to clipboard for older browsers.
      const sharePayload = {
        title: `My ${dest} trip — TravelAI`,
        text: `Check out the ${dest} trip I just planned with TravelAI:`,
        url: shareUrl,
      };
      trackEvent('share_trip_click', { destination: dest, slug: body.data.slug });

      if (navigator.share && navigator.canShare?.(sharePayload)) {
        try {
          await navigator.share(sharePayload);
          setState('idle');
          return;
        } catch {
          // User cancelled — fall through to clipboard
        }
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        setState('copied');
        toast?.success?.(t('chat.shareCopied') || 'Link copied to clipboard');
        setTimeout(() => setState('idle'), 1800);
      } catch {
        // Last-resort: prompt the user with the URL
        window.prompt(t('chat.shareCopyManual') || 'Copy this share link:', shareUrl);
        setState('idle');
      }
    } catch (err) {
      setState('idle');
      toast?.error?.(t('chat.shareFailed') || 'Could not create share link, please try again.');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[ShareTripButton] failed:', err);
      }
    }
  }, [tripPlan, hotels, flights, message, t, toast]);

  if (!tripPlan?.destination) return null;

  const isCopied = state === 'copied';
  const isSharing = state === 'sharing';

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={isSharing}
      data-testid="share-trip-btn"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-60 ${
        isCopied
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
      }`}
    >
      {isSharing ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : isCopied ? (
        <Check className="w-3.5 h-3.5" />
      ) : (
        <Share2 className="w-3.5 h-3.5" />
      )}
      {isCopied
        ? (t('chat.shareCopied') || 'Link copied')
        : (t('chat.shareTrip') || 'Share trip')}
    </button>
  );
}
